import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCustomsCases } from '../../../hooks/useCustomsCases'

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  awaiting_response: 'Awaiting Response',
  c18_received: 'C18 Received',
  appealing: 'Appealing',
  paid: 'Paid',
  closed: 'Closed',
}

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-gray-100 text-gray-700',
  awaiting_response: 'bg-yellow-100 text-yellow-800',
  c18_received: 'bg-orange-100 text-orange-800',
  appealing: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  closed: 'bg-gray-200 text-gray-600',
}

export default function CustomsLetters() {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const { cases, stats, loading, error, isOverdue } = useCustomsCases({ statusFilter, searchQuery })

  if (loading) return <div className="p-6">Loading...</div>
  if (error) return <div className="p-6 text-red-600">{error}</div>

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
        <button
          onClick={() => navigate('/customs')}
          className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
          Customs
        </button>
        <span>/</span>
        <span className="text-gray-900 dark:text-white">Customs Letters</span>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">CFSS Case Management</h1>
        <Link to="/customs/letters/new" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          New Case
        </Link>
      </div>

      {stats && stats.overdue_count > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-red-800">
          <strong>{stats.overdue_count}</strong> case{stats.overdue_count !== 1 ? 's' : ''} {stats.overdue_count === 1 ? 'has' : 'have'} passed their deadline
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Cases</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="text-2xl font-bold text-gray-700">{stats.open}</div>
            <div className="text-sm text-gray-600">Open</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <div className="text-2xl font-bold text-yellow-700">{stats.awaiting_response}</div>
            <div className="text-sm text-gray-600">Awaiting Response</div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <div className="text-2xl font-bold text-orange-700">{stats.c18_received}</div>
            <div className="text-sm text-gray-600">C18 Received</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="text-2xl font-bold text-blue-700">{stats.appealing}</div>
            <div className="text-sm text-gray-600">Appealing</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="text-2xl font-bold text-green-700">£{(stats.total_c18_amount || 0).toLocaleString()}</div>
            <div className="text-sm text-gray-600">Total C18 Amount</div>
          </div>
        </div>
      )}

      <div className="flex gap-4 mb-4">
        <input
          type="text"
          placeholder="Search CFSS ref, C18 ref, importer, EORI..."
          className="flex-1 border rounded px-3 py-2"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <select className="border rounded px-3 py-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="open">Open</option>
          <option value="awaiting_response">Awaiting Response</option>
          <option value="c18_received">C18 Received</option>
          <option value="appealing">Appealing</option>
          <option value="paid">Paid</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3">CFSS Ref</th>
              <th className="text-left px-4 py-3">C18 Ref</th>
              <th className="text-left px-4 py-3">Importer</th>
              <th className="text-left px-4 py-3">Agent</th>
              <th className="text-left px-4 py-3">Deadline</th>
              <th className="text-left px-4 py-3">C18 Amount</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cases.map(c => (
              <tr
                key={c.id}
                className={`border-b hover:bg-gray-50 ${
                  isOverdue(c) ? 'bg-red-50 border-l-4 border-l-red-400' : ''
                }`}
              >
                <td className="px-4 py-3 font-mono text-xs">{c.cfss_number || '-'}</td>
                <td className="px-4 py-3 font-mono text-xs">{c.c18_number || '-'}</td>
                <td className="px-4 py-3">{c.importer_name || '-'}</td>
                <td className="px-4 py-3">{c.agent_name || '-'}</td>
                <td className={`px-4 py-3 ${isOverdue(c) ? 'font-semibold text-red-700' : ''}`}>
                  {c.c18_deadline
                    ? new Date(c.c18_deadline).toLocaleDateString('en-GB')
                    : c.initial_deadline
                      ? new Date(c.initial_deadline).toLocaleDateString('en-GB')
                      : '-'}
                </td>
                <td className="px-4 py-3">{c.c18_amount ? `£${c.c18_amount.toLocaleString()}` : '-'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${STATUS_STYLES[c.status] || STATUS_STYLES.open}`}>
                    {STATUS_LABELS[c.status] || c.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <Link to={`/customs/letters/${c.id}`} className="text-blue-600 hover:underline">
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {cases.length === 0 && <div className="p-8 text-center text-gray-500">No cases found</div>}
      </div>
    </div>
  )
}
